import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { siteInfo } from '@/mocks/site';

const milestones = [
  { year: '2015', title: '问鼎启航', desc: '问鼎下载平台正式上线，以"安全下载"为核心理念，推出首批1000款正版软件资源。' },
  { year: '2016', title: '快速成长', desc: '用户突破500万，与200+软件厂商建立合作，资源库扩充至5万款。' },
  { year: '2018', title: '品类扩展', desc: '新增游戏和应用分类，覆盖全品类下载需求，推出移动端适配版本。' },
  { year: '2020', title: '技术升级', desc: '自研CDN加速网络上线，下载速度提升300%，用户数量突破2000万。' },
  { year: '2022', title: '行业领先', desc: '累计下载量突破10亿次，获评"最受信赖下载平台"，合作厂商超过1000家。' },
  { year: '2024', title: '持续引领', desc: 'AI智能推荐系统上线，资源库突破50万，活跃用户超过3800万，持续引领行业标准。' },
];

const testimonials = [
  { name: '张先生', role: 'IT工程师', content: '用了问鼎下载三年多了，软件都是正版，下载速度也快，从来没中过病毒。装机必备！', rating: 5 },
  { name: '李女士', role: '设计师', content: '在这里找到了很多好用的设计工具，分类清晰，更新及时，比到处找破解版放心多了。', rating: 5 },
  { name: '王同学', role: '大学生', content: '写论文用到的Office和文献管理软件都是在这里下的，正版免费，操作简单，推荐给所有同学了。', rating: 5 },
  { name: '刘先生', role: '游戏玩家', content: '游戏资源很全，下载速度快，还有详细的安装教程。黑神话悟空就是在这下的，体验很棒！', rating: 5 },
  { name: '陈女士', role: '自由职业者', content: '客服响应很快，有一次下载出问题，技术人员远程帮我解决了，服务态度特别好。', rating: 4 },
  { name: '赵先生', role: '企业管理者', content: '公司电脑的软件都是从问鼎下载的，正版授权有保障，不用担心版权问题，非常省心。', rating: 5 },
];

export default function AboutPage() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      setFormData({ name: '', email: '', message: '' });
      return;
    }

    const payload = new FormData(form);
    payload.delete('website_alt');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9tejlrno6agl9ok0f4g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(text); } catch { /* ignore */ }

      if (res.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const msg = (parsed && typeof (parsed as Record<string, unknown>).message === 'string')
          ? (parsed as Record<string, unknown>).message as string
          : '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <Breadcrumb />
      <main>
        {/* Brand Intro */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center mb-14">
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-5">
                关于问鼎下载
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed">
                问鼎下载成立于2015年，是国内领先的正版软件、游戏及应用下载平台。我们始终秉持"安全第一、用户至上"的核心理念，
                致力于为中国用户提供安全可靠、极速便捷的正版下载服务。经过近十年的发展，问鼎下载已与全球超过1000家软件厂商建立合作，
                累计服务超过3800万用户，平台资源总量突破50万款，累计下载量超过12.8亿次。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-14">
              <div className="bg-background-50 rounded-xl p-6 text-center border border-background-200/70">
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className="ri-eye-line text-2xl text-primary-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-900 mb-2">我们的愿景</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  成为中国最值得信赖的正版下载平台，让每一位用户都能安全、便捷地获取所需的正版数字资源。
                </p>
              </div>
              <div className="bg-background-50 rounded-xl p-6 text-center border border-background-200/70">
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-accent-100 flex items-center justify-center">
                  <i className="ri-flag-line text-2xl text-accent-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-900 mb-2">我们的使命</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  构建开放、安全、高效的数字资源分发生态，保护用户免受盗版和恶意软件的侵害，推动正版化进程。
                </p>
              </div>
              <div className="bg-background-50 rounded-xl p-6 text-center border border-background-200/70">
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-secondary-100 flex items-center justify-center">
                  <i className="ri-heart-line text-2xl text-secondary-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-900 mb-2">我们的价值观</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  安全为本、诚信立身、用户至上、持续创新。每一个决策都以用户利益为出发点。
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-primary-200 md:-translate-x-px"></div>
              <div className="space-y-8">
                {milestones.map((m, i) => (
                  <div key={i} className={`relative flex items-start gap-6 md:gap-0 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="hidden md:flex md:w-1/2"></div>
                    <div className="absolute left-2 md:left-1/2 w-5 h-5 rounded-full bg-primary-500 border-4 border-background-100 -translate-x-1/2 z-10"></div>
                    <div className={`ml-10 md:ml-0 md:w-1/2 ${i % 2 === 0 ? 'md:pr-16 md:text-right' : 'md:pl-16 md:text-left'}`}>
                      <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-2">
                        {m.year}
                      </span>
                      <h4 className="text-base font-semibold text-foreground-900 mb-1">{m.title}</h4>
                      <p className="text-sm text-foreground-500 leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                用户口碑
              </h2>
              <p className="text-foreground-500 text-base">
                来自真实用户的评价，是我们前进的最大动力
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <i
                        key={j}
                        className={`text-sm ${j < t.rating ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{t.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center">
                      <span className="text-sm font-bold text-accent-600">{t.name.charAt(0)}</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">{t.name}</div>
                      <div className="text-xs text-foreground-400">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  联系我们
                </h2>
                <p className="text-foreground-500 mb-8 leading-relaxed">
                  如有任何问题或合作意向，欢迎通过以下方式与我们取得联系。
                  我们的客服团队将在工作日9:00-18:00为您服务。
                </p>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800 mb-0.5">电子邮箱</div>
                      <div className="text-sm text-foreground-500">{siteInfo.email}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-lg text-accent-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800 mb-0.5">客服热线</div>
                      <div className="text-sm text-foreground-500">{siteInfo.phone}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-lg text-secondary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800 mb-0.5">公司地址</div>
                      <div className="text-sm text-foreground-500">{siteInfo.address}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70">
                <h3 className="text-lg font-semibold text-foreground-900 mb-5">发送消息</h3>
                <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form>
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200 resize-none"
                      placeholder="请输入您的留言（最多500字）"
                    ></textarea>
                  </div>
                  <div className="website-alt-wrapper">
                    <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <button
                    type="submit"
                    className="w-full py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm transition-all duration-200 whitespace-nowrap"
                  >
                    发送消息
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-accent-600 text-center">消息已发送成功，我们会尽快回复您！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{formError || '提交失败，请稍后重试'}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}